import Divider from "components/screener/Divider";
import ToggableRefinementList from "components/screener/refinements/ToggableRefinementList";
import useInfinitePagination from "lib/hooks/useInfinitePagination";
import { MiniSearchInput } from "lib/miolo";
import deburr from "lodash/fp/deburr";
import orderBy from "lodash/fp/orderBy";
import { useEffect, useState } from "react";
import type { RefinementListProvided } from "react-instantsearch-core";
import { connectRefinementList } from "react-instantsearch-dom";

interface IIndustrySectorRefinementListProps {
  isChoosingSectors: boolean;
  onToggleChooseSectors: (isChoosingSectors: boolean) => void;
}

const IndustrySectorRefinementList = connectRefinementList<IIndustrySectorRefinementListProps & RefinementListProvided>(
  ({ isChoosingSectors, onToggleChooseSectors, items, refine }) => {
    const industrySectors = orderBy(["isRefined", "count", "label"], ["desc", "desc", "asc"], items);

    const [searchQuery, setSearchQuery] = useState<string>("");

    const pagination = useInfinitePagination({
      itemsPerPage: 10,
      items: industrySectors.filter((item) => {
        if (searchQuery && searchQuery.trim()) {
          const cleanSearchQuery = searchQuery.trim();

          return deburr(item.label).toLowerCase().includes(deburr(cleanSearchQuery).toLowerCase());
        }

        return true;
      }),
    });

    useEffect(() => {
      if (searchQuery !== "") {
        pagination.reset();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const refinements = items.filter((item) => item.isRefined);

    if (!isChoosingSectors) {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <h3 className="font-aeonik text-base font-bold uppercase tracking-wide text-zinc-500">Setor</h3>

            {refinements.length > 0 && (
              <div className="flex items-center space-x-1">
                <span className="font-aeonik text-sm font-medium italic tracking-wide text-slate-400">
                  Filtrando por
                </span>

                <strong className="rounded-xl border border-violet-300 bg-violet-50 px-2 py-0.5 text-xs font-bold text-violet-700">
                  {refinements.length} {refinements.length === 1 ? "setor" : "setores"}
                </strong>
              </div>
            )}

            {refinements.length === 0 && (
              <div>
                <span className="flex font-aeonik text-sm font-normal italic tracking-wider text-slate-400">
                  Filtre empresas por setor.
                </span>
              </div>
            )}
          </div>

          <button
            type="button"
            className="font-aeonik text-sm font-medium tracking-wide text-blue-600 hover:underline disabled:text-slate-400"
            onClick={() => onToggleChooseSectors(true)}
            disabled={items.length === 0}
          >
            {refinements.length > 0
              ? `Escolher outros setores (${items.length})`
              : `Escolher setores (${items.length})`}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="font-aeonik text-base font-bold uppercase tracking-wide text-zinc-500">Setor</h3>
          <span className="flex font-aeonik text-sm font-normal tracking-wide text-slate-400">
            Escolha um ou vários setores.
          </span>
        </div>

        <MiniSearchInput
          value={searchQuery}
          placeholder="Pesquisar"
          onChange={(searchQuery) => setSearchQuery(searchQuery)}
        />

        {pagination.items.length > 0 && (
          <>
            <ToggableRefinementList items={pagination.items} refine={refine} />

            {pagination.hasMoreToShow && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={pagination.showMore}
                  className="font-aeonik text-sm font-medium tracking-wide text-sky-600 hover:underline"
                >
                  Ver mais setores
                </button>
              </div>
            )}

            <Divider variant="faded" />

            <div className="text-center">
              <button
                type="button"
                className="font-aeonik text-sm font-medium tracking-wide text-violet-600 hover:underline"
                onClick={() => {
                  onToggleChooseSectors(false);
                  pagination.reset();
                }}
              >
                Ver todos os filtros
              </button>
            </div>
          </>
        )}
      </div>
    );
  },
);

interface IIndustrySectorProps {
  isChoosingSectors: boolean;
  onToggleChooseSectors: (isChoosingSectors: boolean) => void;
}

export default function IndustrySector({ isChoosingSectors, onToggleChooseSectors }: IIndustrySectorProps) {
  return (
    <IndustrySectorRefinementList
      isChoosingSectors={isChoosingSectors}
      onToggleChooseSectors={onToggleChooseSectors}
      attribute="industrySector"
      limit={180}
    />
  );
}
